import { Controller } from "@hotwired/stimulus";

// Polls the server-side trim status while a video is still processing on AWS and
// reloads the page once it is finalized, so the "processing" placeholder is
// replaced by the real video without a manual refresh.
//
// Safety-first: it self-schedules with setTimeout (never overlapping requests),
// aborts any in-flight request and clears its timer on disconnect, backs off,
// caps the total number of attempts, and stops on a terminal error, on an
// auth/gate/not-found response, or after repeated failures — so it never
// hammers the server or leaves anything hanging.
export default class extends Controller {
  static values = {
    statusUrl: String,
    interval: { type: Number, default: 5000 },
    maxInterval: { type: Number, default: 20000 },
    maxAttempts: { type: Number, default: 120 },
    maxFailures: { type: Number, default: 5 },
  };

  static targets = ["message"];

  connect() {
    if (!this.hasStatusUrlValue || !this.statusUrlValue) return;
    this.stopped = false;
    this.attempts = 0;
    this.failures = 0;
    this.currentInterval = this.intervalValue;
    this.scheduleNext();
  }

  disconnect() {
    this.stop();
  }

  stop() {
    this.stopped = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  scheduleNext() {
    if (this.stopped) return;
    this.timer = setTimeout(() => this.poll(), this.currentInterval);
  }

  async poll() {
    if (this.stopped) return;
    this.attempts += 1;
    if (this.attempts > this.maxAttemptsValue) {
      this.stop();
      return;
    }

    this.abortController = new AbortController();
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal,
      });
      this.abortController = null;

      // Auth / capability gate / not-found: stop quietly. Nothing this poller can
      // do; a manual refresh remains available for whoever can see the video.
      if ([401, 403, 404].includes(response.status)) {
        this.stop();
        return;
      }
      if (!response.ok) {
        this.handleFailure();
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (data && data.finalized) {
        this.reload();
        return;
      }
      if (data && data.phase === "error") {
        this.showError();
        this.stop();
        return;
      }

      // Still processing: reset the failure streak and back off a little.
      this.failures = 0;
      this.currentInterval = Math.min(
        Math.round(this.currentInterval * 1.2),
        this.maxIntervalValue
      );
      this.scheduleNext();
    } catch (error) {
      if (error.name === "AbortError") return; // disconnected mid-flight
      this.abortController = null;
      this.handleFailure();
    }
  }

  handleFailure() {
    this.failures += 1;
    if (this.failures >= this.maxFailuresValue) {
      this.stop();
      return;
    }
    this.scheduleNext();
  }

  reload() {
    this.stop();
    window.location.reload();
  }

  showError() {
    if (this.hasMessageTarget) {
      this.messageTarget.textContent =
        "We couldn't finish processing your video. Please try uploading it again.";
    }
  }
}
